import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, ActivityIndicator, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { InsetModal as Modal } from './InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useThemedStyles } from '../hooks/useThemedStyles';

export interface ExportField {
  id: string;
  label: string;
  /** True for the phone/mobile field specifically — shown with a small
   * "masked unless you have permission" hint rather than hidden, matching
   * how contacts.viewMobileNumber behaves everywhere else in the app
   * (mask the value, never hide the column). */
  maskedForCurrentUser?: boolean;
}

interface ContactExportFieldsModalProps {
  visible: boolean;
  onClose: () => void;
  fixedFields: ExportField[];
  customFieldKeys: string[];
  onConfirm: (selectedFieldIds: string[], format: 'csv' | 'xlsx') => void;
  exporting: boolean;
}

const DEFAULT_SELECTED_FIXED_IDS = ['name', 'phone', 'email', 'tags', 'city', 'country', 'nationality'];

/**
 * Field picker shown after choosing "current selection"/"all contacts" in
 * ContactsExportDialog — lets the user choose exactly which columns land in
 * the export (fixed fields + any custom/extra field seen on this business's
 * contacts) and which file format to produce. Full-screen for the same
 * reason the sort/filter modal is (ContactSortFilterModal.tsx): a field
 * list that can include many custom fields needs real room, not a small
 * anchored dropdown.
 *
 * Format defaults to Excel (.xlsx) rather than CSV — CSV's plain-text
 * encoding is reader-dependent (some Excel versions/locales misdetect
 * UTF-8 even with a BOM present, showing Arabic content as mojibake); .xlsx
 * has no such ambiguity since it's a structured binary format, not text a
 * reader has to guess the encoding of. CSV stays available for anyone who
 * specifically wants it (e.g. importing into another tool).
 */
export const ContactExportFieldsModal: React.FC<ContactExportFieldsModalProps> = ({
  visible,
  onClose,
  fixedFields,
  customFieldKeys,
  onConfirm,
  exporting,
}) => {
  const styles = useThemedStyles(getStyles);
  const insets = useSafeAreaInsets();
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const [selected, setSelected] = useState<Set<string>>(new Set(DEFAULT_SELECTED_FIXED_IDS));
  const [format, setFormat] = useState<'csv' | 'xlsx'>('xlsx');

  useEffect(() => {
    if (visible) {
      setSelected(new Set(DEFAULT_SELECTED_FIXED_IDS));
      setFormat('xlsx');
    }
  }, [visible]);

  const toggleField = (fieldId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(fieldId)) next.delete(fieldId);
      else next.add(fieldId);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set([...fixedFields.map((f) => f.id), ...customFieldKeys]));
  const selectNone = () => setSelected(new Set());

  const canConfirm = selected.size > 0 && !exporting;

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      {/* Full-screen page inside a transparent Modal — React Native adds no
          safe-area padding, so keep the header clear of the notch/status
          bar and the footer clear of the home indicator. */}
      <View style={[styles.page, { paddingTop: insets.top, paddingBottom: insets.bottom }]}>
        <View style={[styles.header, { flexDirection: rtl.rowDirection }]}>
          <TouchableOpacity onPress={onClose} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name={rtl.isRTL ? 'arrow-forward' : 'arrow-back'} size={24} color={colors.text.primary} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { textAlign: 'center' }]}>{t('contactsScreen.export.fieldsModalTitle')}</Text>
          <View style={{ width: 24 }} />
        </View>

        <View style={[styles.quickActionsRow, { flexDirection: rtl.rowDirection }]}>
          <TouchableOpacity onPress={selectAll}>
            <Text style={styles.quickActionText}>{t('contactsScreen.export.selectAll')}</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={selectNone}>
            <Text style={styles.quickActionText}>{t('contactsScreen.export.selectNone')}</Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('contactsScreen.export.fixedFieldsTitle')}</Text>
            {fixedFields.map((field) => {
              const isSelected = selected.has(field.id);
              return (
                <TouchableOpacity
                  key={field.id}
                  style={[styles.row, { flexDirection: rtl.rowDirection }]}
                  onPress={() => toggleField(field.id)}
                >
                  <View style={[styles.checkbox, isSelected && styles.checkboxSelected]}>
                    {isSelected && <Ionicons name="checkmark" size={14} color={colors.white} />}
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.rowText, { textAlign: rtl.textStart }]}>{field.label}</Text>
                    {field.maskedForCurrentUser && (
                      <Text style={[styles.rowHint, { textAlign: rtl.textStart }]}>{t('contactsScreen.export.maskedHint')}</Text>
                    )}
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>

          {customFieldKeys.length > 0 && (
            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('contacts.sort.extraFieldsTitle')}</Text>
              {customFieldKeys.map((key) => {
                const isSelected = selected.has(key);
                return (
                  <TouchableOpacity
                    key={key}
                    style={[styles.row, { flexDirection: rtl.rowDirection }]}
                    onPress={() => toggleField(key)}
                  >
                    <View style={[styles.checkbox, isSelected && styles.checkboxSelected]}>
                      {isSelected && <Ionicons name="checkmark" size={14} color={colors.white} />}
                    </View>
                    <Text style={[styles.rowText, { textAlign: rtl.textStart }, { flex: 1 }]} numberOfLines={1}>{key}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          )}

          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('contactsScreen.export.formatTitle')}</Text>
            <View style={[styles.formatRow, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity
                style={[styles.formatOption, format === 'xlsx' && styles.formatOptionActive]}
                onPress={() => setFormat('xlsx')}
              >
                <Ionicons name="grid-outline" size={18} color={format === 'xlsx' ? colors.white : colors.primary} />
                <Text style={[styles.formatOptionText, format === 'xlsx' && styles.formatOptionTextActive]}>{t('contactsScreen.export.formatExcel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.formatOption, format === 'csv' && styles.formatOptionActive]}
                onPress={() => setFormat('csv')}
              >
                <Ionicons name="document-text-outline" size={18} color={format === 'csv' ? colors.white : colors.primary} />
                <Text style={[styles.formatOptionText, format === 'csv' && styles.formatOptionTextActive]}>{t('contactsScreen.export.formatCsv')}</Text>
              </TouchableOpacity>
            </View>
            {format === 'csv' && (
              <Text style={[styles.formatHint, { textAlign: rtl.textStart }]}>{t('contactsScreen.export.csvEncodingHint')}</Text>
            )}
          </View>
        </ScrollView>

        <View style={styles.footer}>
          <TouchableOpacity
            style={[styles.confirmButton, !canConfirm && styles.confirmButtonDisabled]}
            onPress={() => onConfirm([...selected], format)}
            disabled={!canConfirm}
          >
            {exporting ? (
              <ActivityIndicator size="small" color={colors.white} />
            ) : (
              <Text style={styles.confirmButtonText}>{t('contactsScreen.export.downloadButton', { count: selected.size })}</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: colors.background.surface,
    ...(Platform.OS === 'web' && { maxWidth: 640, alignSelf: 'center', width: '100%' }),
  },
  header: {
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerTitle: {
    flex: 1,
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.text.primary,
  },
  quickActionsRow: {
    gap: spacing.lg,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  quickActionText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontWeight: '600',
  },
  body: {
    flex: 1,
  },
  bodyContent: {
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.xl,
  },
  section: {
    marginBottom: spacing.lg,
  },
  sectionTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: '700',
    color: colors.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.sm,
  },
  row: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.md,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 5,
    borderWidth: 2,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSelected: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  rowText: {
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
  },
  rowHint: {
    fontSize: typography.fontSize.xs,
    color: colors.text.disabled,
    marginTop: 2,
  },
  formatRow: {
    gap: spacing.sm,
  },
  formatOption: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.md,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surfaceRaised,
  },
  formatOptionActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  formatOptionText: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.primary,
  },
  formatOptionTextActive: {
    color: colors.white,
  },
  formatHint: {
    fontSize: typography.fontSize.xs,
    color: colors.text.disabled,
    marginTop: spacing.sm,
  },
  footer: {
    padding: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  confirmButton: {
    backgroundColor: colors.primary,
    borderRadius: spacing.borderRadius.md,
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  confirmButtonDisabled: {
    opacity: 0.5,
  },
  confirmButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.base,
    fontWeight: '700',
  },
});
